// src/contexts/AuthContext.js
import React, { useContext, useEffect, useState } from "react";

import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { doc, getDoc, setDoc,updateDoc } from "firebase/firestore";

import { auth, db } from "../firebase";
import i18n from "../i18n";

const AuthContext = React.createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const normalizeLanguage = language => {
    return language && language.toLowerCase().startsWith("fr") ? "fr" : "en";
  };

  const applyUserLanguage = async language => {
    const normalizedLanguage = normalizeLanguage(
      language || i18n.resolvedLanguage
    );
    if (i18n.resolvedLanguage !== normalizedLanguage) {
      await i18n.changeLanguage(normalizedLanguage);
    }
    return normalizedLanguage;
  };

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password).then(
      async userCredential => {
        const user = userCredential.user;
        const language = normalizeLanguage(i18n.resolvedLanguage);
        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          isActive: false, // Compte inactif par défaut
          createdAt: new Date(),
          language
        });

        // Envoi de l'email de vérification
        await sendEmailVerification(user);
        return user;
      }
    );
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password).then(
      userCredential => {
        // Fetch user data from Firestore
        const user = userCredential.user;
        return getUserData(user.uid);
      }
    );
  };

  const logout = async () => {
    await signOut(auth);
    localStorage.removeItem("userData");
    setCurrentUser(null);
    setIsAdmin(false);
  };

  const getUserData = async uid => {
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const userData = docSnap.data();
      userData.uid = uid;
      userData.language = await applyUserLanguage(userData.language);

      // Vérification si le compte est actif
      if (!userData.isActive) {
        await logout(); // Déconnexion immédiate si le compte n'est pas actif
        throw new Error("Votre compte doit être validé.");
      }

      // Vérification du claim admin
      const idTokenResult = await auth.currentUser.getIdTokenResult();
      setIsAdmin(!!idTokenResult.claims.admin);

      localStorage.setItem("userData", JSON.stringify(userData));
      setCurrentUser(userData);
    } else {
      throw new Error("Erreur de connexion : Document utilisateur introuvable.");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        // Restauration rapide depuis localStorage si disponible
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          parsedUserData.language = await applyUserLanguage(parsedUserData.language);
          setCurrentUser(parsedUserData);
          localStorage.setItem("userData", JSON.stringify(parsedUserData));
        } else {
          await getUserData(user.uid);
        }
        // Vérification du claim admin depuis le token Firebase (source de vérité)
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(!!idTokenResult.claims.admin);
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const updateUserData = async newData => {
    if (currentUser) {
      const userDoc = doc(db, `users/${currentUser.uid}`);
      const dataToUpdate = { ...newData };

      if (Object.prototype.hasOwnProperty.call(dataToUpdate, "language")) {
        dataToUpdate.language = normalizeLanguage(dataToUpdate.language);
      }

      await updateDoc(userDoc, dataToUpdate);
      if (dataToUpdate.language) {
        await applyUserLanguage(dataToUpdate.language);
      }

      const updatedUser = { ...currentUser, ...dataToUpdate };
      setCurrentUser(updatedUser);
      localStorage.setItem("userData", JSON.stringify(updatedUser));
    }
  };

  const value = {
    currentUser,
    isAdmin,
    updateUserData,
    signup,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={ value }>
      { !loading && children }
    </AuthContext.Provider>
  );
};
